import { useEffect, useRef, useState } from "react";
import { Info as InfoIcon } from "lucide-react";
import { sfx } from "@/audio";
import { ACC, fmt } from "./util";

// The look lives in index.css (.hud-panel, .chip-btn, .acc-*, .reel); these are its React handles.
export const PANEL = "hud-panel";

// Every chip button clicks, wherever it is. On window so it runs after the audio engine's first-tap unlock.
window.addEventListener("pointerdown", (e) => e.target.closest?.(".chip-btn:not(:disabled)") && sfx("click"));

// A flat button: the primary (the accent), soft (a grey step), or the pale tint of a category (blue, green, gold, rose).
export const Btn = ({ tone = "primary", className = "", style, ...p }) => (
  <button type="button" {...p} style={ACC[tone] ? { "--acc": ACC[tone], ...style } : style}
    className={`chip-btn ${tone === "soft" ? "chip-soft" : ACC[tone] ? "chip-tint" : ""} px-3 py-1.5 text-sm whitespace-nowrap ${className}`} />
);

export const Uxd = ({ n, className = "" }) => (
  <span className={`money font-semibold tabular-nums ${className}`}>{fmt(n)} <span className="text-[0.75em] font-medium text-x-mute">UXD</span></span>
);

// A small pale badge with the dark of its own hue as ink.
export const Pill = ({ acc = "gold", className = "", style, ...p }) => (
  <span {...p} style={{ "--acc": ACC[acc], ...style }}
    className={`acc-tint inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums ${className}`} />
);

export const Bar = ({ value, color = ACC.gold }) => (
  <div className="hud-well h-2 overflow-hidden rounded-full">
    <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%`, background: color }} />
  </div>
);

export const Skel = ({ className = "" }) => <div className={`rounded-[10px] bg-x-raised ${className}`} />;

// A progress ring that sweeps as `value` (0..1) changes; children sit in the middle.
export function Ring({ value, size = 56, stroke = 5, acc = "blue", className = "", children }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <span className={`relative grid shrink-0 place-items-center ${className}`} style={{ width: size, height: size, "--acc": ACC[acc] }}>
      <svg className="absolute inset-0 -rotate-90" width={size} height={size} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} style={{ stroke: "var(--x-active)" }} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0, Math.min(1, value)))}
          style={{ stroke: "var(--acc)", transition: "stroke-dashoffset 1s linear" }} />
      </svg>
      {children}
    </span>
  );
}

// The small (i): the explanation lives behind it, not in the panel.
export function Info({ text, className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <span className={`relative inline-flex ${className}`}>
      <button type="button" data-pop aria-label="Info" title={text} onClick={() => setOpen(!open)}
        className="grid h-5 w-5 place-items-center rounded-full bg-x-raised text-x-mute transition-colors hover:bg-x-active hover:text-x-ink">
        <InfoIcon className="h-3 w-3" strokeWidth={2.5} />
      </button>
      {open && <Pop onClose={() => setOpen(false)} className="absolute right-0 top-full mt-1.5 w-60 px-3 py-2 text-left text-sm font-normal normal-case text-x-ink">{text}</Pop>}
    </span>
  );
}

// A drawer section: a plain title in ink, its small grey icon, and the explanation behind (i). `accent` colours its data.
export const Section = ({ id, icon: Icon, title, right, info, accent = "gold", children }) => (
  <section data-section={id} style={{ "--acc": ACC[accent] }} className="border-b border-x-rule px-4 py-4 last:border-0">
    <div className="mb-3 flex items-center gap-2">
      {Icon && <Icon className="h-4 w-4 text-x-mute" strokeWidth={2} />}
      <h3 className="text-base font-semibold text-x-ink">{title}</h3>
      <span className="ml-auto flex items-center gap-1.5 text-sm text-x-mute">{right}{info && <Info text={info} />}</span>
    </div>
    {children}
  </section>
);

// A number as a slot reel: each digit rolls to its place when the value changes (instantly with reduced motion).
export function Reel({ value, className = "" }) {
  const full = fmt(value);
  const [shown, setShown] = useState(() => full.replace(/\d/g, "0"));
  useEffect(() => { setShown(full); }, [full]);
  return (
    <span className={`reel tabular-nums ${className}`}>
      <span className="sr-only">{full}</span>
      {[...shown].reverse().map((c, i) => (/\d/.test(c)
        ? <span key={i} aria-hidden="true" className="reel-d" style={{ "--d": c, "--delay": `${i * 0.07}s` }} />
        : <span key={i} aria-hidden="true">{c}</span>))}
    </span>
  );
}

// A floating panel that closes on a tap outside; its trigger button carries data-pop.
export function Pop({ onClose, className = "", children }) {
  const ref = useRef();
  useEffect(() => {
    const away = (e) => !ref.current?.contains(e.target) && !e.target.closest("[data-pop]") && onClose();
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [onClose]);
  return <div ref={ref} className={`${PANEL} hud-float pop pointer-events-auto z-30 rounded-2xl p-2 ${className}`}>{children}</div>;
}
